import { useState, type JSX } from 'react';
import { ClipboardList, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isVaultNotConfigured, useOutreachAccounts } from '@/hooks/useOutreachAccounts';
import type { OutreachInviter } from '@/api/types';
import { AccountFormDialog } from './AccountFormDialog';
import { AccountsTable } from './AccountsTable';
import { BulkAddDialog } from './BulkAddDialog';
import { VaultNotConfigured } from './VaultNotConfigured';

/**
 * "Accounts" on an outreacher's admin profile: the logins they message
 * creators from. Admin-only — outreachers never see this, not even their own.
 */
export function OutreacherAccountsSection({ inviter }: { inviter: OutreachInviter }): JSX.Element {
  const accounts = useOutreachAccounts({ inviterId: inviter.publicId });
  const [adding, setAdding] = useState(false);
  const [pasting, setPasting] = useState(false);
  const notConfigured = isVaultNotConfigured(accounts.error);
  const rows = accounts.data?.accounts ?? [];
  const fixedInviter = { publicId: inviter.publicId, name: inviter.name };

  return (
    <section aria-labelledby="accounts-h" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="accounts-h" className="text-lg font-medium">
          Accounts
          {accounts.data && rows.length > 0 && (
            <span className="ml-2 text-sm font-normal text-muted-foreground">{rows.length}</span>
          )}
        </h2>
        {!notConfigured && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setPasting(true)}>
              <ClipboardList className="h-3.5 w-3.5" /> Paste many
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
              <Plus className="h-3.5 w-3.5" /> Add account
            </Button>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        The logins {inviter.name} messages creators from. Only admins can see these; {inviter.name} can&apos;t.
      </p>
      {notConfigured ? (
        <VaultNotConfigured />
      ) : accounts.error ? (
        <p role="alert" className="text-sm text-destructive">
          Couldn't load accounts: {(accounts.error as Error).message}
        </p>
      ) : (
        <AccountsTable
          accounts={rows}
          loading={accounts.isLoading}
          emptyHint={`Add the Instagram, TikTok and other logins ${inviter.name} uses, one at a time or pasted as a list.`}
        />
      )}
      <AccountFormDialog open={adding} onOpenChange={setAdding} fixedInviter={fixedInviter} />
      <BulkAddDialog open={pasting} onOpenChange={setPasting} fixedInviter={fixedInviter} />
    </section>
  );
}
